import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock } from "lucide-react";
import { BUTTON, CHOSEN, KIT, chatT, kitPanel, longDate, partyPhrase, shortDate } from "./kit-tokens";

export type KitTimes = { slug: string; date: string; slots: { time: string }[] };

/** One test for "is this a restaurant" across the chat: layout-parts owns it. */
export { isRestaurantRef } from "../layout-parts";

/**
 * C22: the open times for one place on one day, as a 3-column grid.
 *
 * Picking a time sends "Book <place> at <time> on <date> for N" in the
 * chat's language; the agent answers with a booking proposal (C10). Nothing
 * is reserved from here. No "full" state: we have no real capacity data, so
 * a crossed-out time would be invented. Restaurant tables are free, so a
 * restaurant says so and never shows an amount.
 */
export function TimeSlots({
  times,
  placeName,
  lang,
  onSend,
  adults,
  children = 0,
  isRestaurant = false,
  locked = false,
}: {
  times: KitTimes;
  /** The place's name, from the turn's matches; falls back to the slug. */
  placeName?: string | null;
  lang: string;
  onSend: (text: string) => void;
  /** The party already agreed in the chat, when known. */
  adults?: number | null;
  children?: number;
  isRestaurant?: boolean;
  locked?: boolean;
}) {
  const { t: baseT, i18n } = useTranslation();
  const t = chatT(i18n, baseT, lang);
  const [picked, setPicked] = useState<string | null>(null);
  if (times.slots.length === 0) return null;

  const place = placeName?.trim() || times.slug;
  const party = adults && adults > 0 ? partyPhrase(t, adults, children) : null;
  const done = locked || picked !== null;

  const pick = (time: string) => {
    if (done) return;
    setPicked(time);
    const date = longDate(times.date, lang);
    onSend(
      party
        ? t("ai_concierge.kit.times.send", { place, time, date, party })
        : t("ai_concierge.kit.times.send_no_party", { place, time, date }),
    );
  };

  return (
    <div data-testid="kit-times" className="flex flex-col gap-2.5 p-3" style={kitPanel()}>
      <div className="flex items-start justify-between gap-2">
        <p
          className="min-w-0 text-[11px] font-bold uppercase tracking-[0.1em]"
          style={{ color: KIT.muted }}
        >
          {[place, shortDate(times.date, lang), party].filter(Boolean).join(" · ")}
        </p>
        {isRestaurant ? (
          <span className="shrink-0 text-[11.5px] font-semibold" style={{ color: KIT.green }}>
            {t("ai_concierge.kit.times.free_to_reserve")}
          </span>
        ) : null}
      </div>
      <div role="group" aria-label={t("ai_concierge.kit.times.title", { place })} className="grid grid-cols-3 gap-1.5">
        {times.slots.map(({ time }) => {
          const on = picked === time;
          return (
            <button
              key={time}
              type="button"
              aria-pressed={on}
              disabled={done && !on}
              onClick={() => pick(time)}
              className="inline-flex min-h-[40px] items-center justify-center gap-1 rounded-[11px] text-[13.5px] font-semibold tabular-nums transition-opacity duration-200 disabled:opacity-40"
              style={on ? CHOSEN : { ...BUTTON, color: KIT.navy }}
            >
              {on ? <Clock size={12} aria-hidden /> : null}
              <span dir="ltr">{time}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
